import { RotateCcw } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BlockedAction } from '@/components/custom-ui/BlockedAction'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'

export interface ReprocessButtonProps {
  onClick: () => void
  loading: boolean
  /**
   * Faces of this document confirmed to depict a person. Re-extraction deletes
   * the document's faces and their decisions, so above zero the button asks
   * first and says how many confirmed matches would be lost.
   */
  confirmedFaceCount?: number
  /** A HUMINT capture: immutable evidence, so reprocessing is disabled and the tooltip says why. */
  locked?: boolean
}

/** True when re-extraction would throw away user decisions. */
export function needsReprocessConfirmation(confirmedFaceCount: number | undefined): boolean {
  return (confirmedFaceCount ?? 0) > 0
}

export function ReprocessButton({
  onClick,
  loading,
  confirmedFaceCount,
  locked = false,
}: ReprocessButtonProps) {
  const { t } = useTranslation()
  const [confirming, setConfirming] = useState(false)
  const guarded = needsReprocessConfirmation(confirmedFaceCount)

  const handleClick = () => {
    if (guarded) {
      setConfirming(true)
      return
    }
    onClick()
  }

  const handleConfirm = () => {
    setConfirming(false)
    onClick()
  }

  return (
    <>
      <BlockedAction reason={locked ? t('documents.capture.immutable') : null}>
        <Button
          type="button"
          variant="outline"
          size="xs"
          className="max-md:h-9"
          onClick={handleClick}
          loading={loading}
          disabled={locked}
          title={locked ? undefined : t('documents.detail.reprocessTooltip')}
        >
          <RotateCcw />
          <span>{t('documents.detail.reprocess')}</span>
        </Button>
      </BlockedAction>
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('documents.detail.reprocessFacesTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('documents.detail.reprocessFacesWarning', { count: confirmedFaceCount ?? 0 })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>
              {t('documents.detail.reprocessConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
